import React, { useMemo } from "react";
import { ACTION_CODES } from "../../constants/permissions";

export interface SplitTransactionReviewItem {
  amount: number;
  description: string;
}

export interface SplitTransactionAmountRawJson {
  transaction_amount?: number;
  currency_symbol?: string;
  description?: string;
  reason?: string;
  split_items?: SplitTransactionReviewItem[];
}

export interface SplitTransactionAmountReviewViewProps {
  rawJson: SplitTransactionAmountRawJson | null;
  fallbackAmount?: number;
  fallbackCurrency?: string;
  fallbackDescription?: string;
}

interface ReviewPopulationLike {
  action_code?: string;
  change_details?: {
    raw_json?: SplitTransactionAmountRawJson;
  };
}

export const isSplitTransactionAmountReview = (
  reviewForPopulation: ReviewPopulationLike | null,
): boolean => {
  if (!reviewForPopulation) {
    return false;
  }

  if (
    reviewForPopulation.action_code === ACTION_CODES.SPLIT_TRANSACTION_AMOUNT
  ) {
    return true;
  }

  return Array.isArray(
    reviewForPopulation.change_details?.raw_json?.split_items,
  );
};

const EMPTY_SPLIT_ITEMS: SplitTransactionReviewItem[] = [];

const formatCurrencyAmount = (
  amount: unknown,
  currencySymbol: string,
): string => {
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount)) {
    return "N/A";
  }

  return `${currencySymbol}${numericAmount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const SplitTransactionAmountReviewView: React.FC<
  SplitTransactionAmountReviewViewProps
> = ({
  rawJson,
  fallbackAmount,
  fallbackCurrency = "₹",
  fallbackDescription,
}) => {
  const splitItems = rawJson?.split_items ?? EMPTY_SPLIT_ITEMS;
  const currencySymbol = rawJson?.currency_symbol ?? fallbackCurrency;
  const originalAmount = rawJson?.transaction_amount ?? fallbackAmount ?? 0;
  const description = rawJson?.description ?? fallbackDescription;

  const splitTotal = useMemo(
    () =>
      splitItems.reduce(
        (sum, item) =>
          sum +
          (Number.isFinite(Number(item.amount)) ? Number(item.amount) : 0),
        0,
      ),
    [splitItems],
  );

  if (!splitItems.length) {
    return (
      <div className="flex w-full flex-col items-start gap-2 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-4">
        <span className="text-body font-body text-subtext-color">
          No split details are available for this review request.
        </span>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-4">
      <div className="flex w-full flex-col gap-2">
        <span className="text-heading-4 font-heading-4 text-default-font">
          Split Transaction Amount
        </span>
        <span className="text-caption font-caption text-subtext-color">
          {splitItems.length} split item{splitItems.length === 1 ? "" : "s"}{" "}
          requested for approval
        </span>
      </div>

      <div className="flex w-full flex-col gap-3 rounded-md border border-solid border-neutral-border bg-default-background px-4 py-3">
        {description && (
          <div className="flex w-full items-start justify-between gap-3">
            <span className="text-body font-body text-subtext-color">
              Original Description
            </span>
            <span className="text-body font-body text-default-font text-right">
              {description}
            </span>
          </div>
        )}
        <div className="flex w-full items-center justify-between gap-3">
          <span className="text-body font-body text-subtext-color">
            Original Amount
          </span>
          <span className="text-body-bold font-body-bold text-default-font">
            {formatCurrencyAmount(originalAmount, currencySymbol)}
          </span>
        </div>
      </div>

      <div className="flex w-full flex-col gap-2">
        {splitItems.map((item, index) => (
          <div
            key={`split-item-${index}`}
            className="flex w-full flex-col gap-2 rounded-md border border-solid border-neutral-border bg-default-background px-3 py-2"
          >
            <div className="flex w-full items-center justify-between gap-3">
              <span className="text-body-bold font-body-bold text-default-font">
                Item {index + 1}
              </span>
              <span className="text-body-bold font-body-bold text-default-font">
                {formatCurrencyAmount(item.amount, currencySymbol)}
              </span>
            </div>
            <div className="flex w-full items-start justify-between gap-3">
              <span className="text-body font-body text-subtext-color">
                Description
              </span>
              <span className="text-body font-body text-default-font text-right">
                {item.description?.trim() || "N/A"}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
